import * as Three from 'three';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';
import toast from 'react-hot-toast';
import { selectObjectInScene } from '../../pages/Dashboard/GridPage';
import { setLoadingMessage } from '../../redux/Slice/globalStates';
import {
  add_new_assets,
  update_assets_data_new,
  addModel,
  addSceneMesh,
  selected_object,
  set_is_glb_image_editoropen,
  setPushIntersectedObject,
} from '../../redux/Slice/gridSlice';
import store from '../../redux/store';
import { getNextCopyName, buildTempFileName } from '../../utils/NamingUtils';
import { UpdateMaterial, normalizeImportedMeshMaterialsForExport } from './materialFunction';
import { handleMeshUpload } from './Mesh';
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter';
import { isolateDuplicatePublishIdentity } from '../../pages/Training/utils/gridpageUtils';
import { attachClonedEventsToDuplicate } from '../../utils/eventAssetUtils';

// ──────────────────────────────────────────────────────────────
// TEXTURE PIPELINE – ported from Main.js
// ──────────────────────────────────────────────────────────────

/**
 * Returns every possible lookup key for a given file path string.
 * Covers: full relative path, every suffix segment, filename, basename —
 * each in original and lowercase form.  This lets findTexture() resolve
 * references like "textures/body.png", "body.png", or "body" to the same
 * cached texture regardless of how the MTL or OBJLoader spelled it.
 */
function getAllKeys(pathStr) {
  if (!pathStr) return [];
  const norm = pathStr.replace(/\\/g, '/').trim();
  const parts = norm.split('/');
  const filename = parts[parts.length - 1];
  const basename = filename.replace(/\.[^/.]+$/, '');
  const keys = new Set();
  for (let i = 0; i < parts.length; i++) {
    const suffix = parts.slice(i).join('/');
    keys.add(suffix);
    keys.add(suffix.toLowerCase());
  }
  keys.add(filename);
  keys.add(filename.toLowerCase());
  keys.add(basename);
  keys.add(basename.toLowerCase());
  return Array.from(keys).filter(Boolean);
}

/**
 * Store a texture under every key variant of its path so that any reference
 * style will hit the cache.
 */
function storeTexture(materialLib, pathStr, tex) {
  for (const k of getAllKeys(pathStr)) {
    materialLib[k] = tex;
  }
}

/**
 * Look up a texture by any key variant of the given reference string.
 * Returns the texture if found, otherwise null.
 */
function findTexture(materialLib, ref) {
  if (!ref) return null;
  for (const k of getAllKeys(ref)) {
    if (materialLib[k]) return materialLib[k];
  }
  return null;
}

/**
 * Parse an MTL file text into a map of materialName → props.
 * Captures: map_Kd (diffuse texture), Kd (color), Ns (shininess),
 * d / Tr (opacity), map_Ks, map_Bump.
 */
function parseMTL(text) {
  const mats = {};
  let cur = null;
  for (let line of text.split('\n')) {
    line = line.trim();
    if (!line || line.startsWith('#')) continue;
    const parts = line.split(/\s+/);
    if (parts[0] === 'newmtl') {
      cur = parts.slice(1).join(' ');
      mats[cur] = {};
    } else if (cur) {
      // Strip option flags (e.g. -bm, -o) so we get the bare filename
      const texArg = () =>
        parts
          .slice(1)
          .filter((p) => !p.startsWith('-'))
          .join(' ')
          .trim();
      if (parts[0] === 'map_Kd')                        mats[cur].map      = texArg();
      else if (parts[0] === 'map_Ka')                    mats[cur].ambMap   = texArg();
      else if (parts[0] === 'map_Ks')                    mats[cur].specMap  = texArg();
      else if (parts[0] === 'map_Bump' || parts[0] === 'bump') mats[cur].bumpMap = texArg();
      else if (parts[0] === 'Kd')   mats[cur].color     = [parseFloat(parts[1]), parseFloat(parts[2]), parseFloat(parts[3])];
      else if (parts[0] === 'Ns')   mats[cur].shininess  = parseFloat(parts[1]);
      else if (parts[0] === 'd')    mats[cur].opacity    = parseFloat(parts[1]);
      else if (parts[0] === 'Tr')   mats[cur].opacity    = 1 - parseFloat(parts[1]);
    }
  }
  return mats;
}

/**
 * Load a single image File into a Three.js Texture, index it under every
 * key variant of its path, and return the texture.
 * Settings mirror Main.js exactly: RepeatWrapping, LinearMipMap, SRGBColorSpace,
 * max anisotropy, and flipY = false (V-flip is handled in UV coords by OBJLoader).
 */
function loadTextureFromFile(materialLib, file, pathStr, renderer) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      new Three.TextureLoader().load(
        e.target.result,
        (tex) => {
          tex.wrapS = Three.RepeatWrapping;
          tex.wrapT = Three.RepeatWrapping;
          tex.minFilter = Three.LinearMipMapLinearFilter;
          tex.magFilter = Three.LinearFilter;
          if (renderer) {
            tex.anisotropy = renderer.capabilities.getMaxAnisotropy();
          }
          tex.colorSpace = Three.SRGBColorSpace;
          // flipY = true is the Three.js default and CORRECT for OBJLoader.
          // OBJLoader already flips V internally (1 - v), so we must NOT set
          // flipY = false here — that would leave UVs un-corrected and render
          // the texture upside-down. Only hand-rolled parsers (like Main.js)
          // that do their own 1-v flip need flipY = false.
          tex.flipY = true;
          tex.needsUpdate = true;
          storeTexture(materialLib, pathStr, tex);
          resolve(tex);
        },
        undefined,
        reject
      );
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Given all uploaded files, build a fully-resolved materialLib (texture cache)
 * and an mtlData map (material name → props).
 * Returns { materialLib, mtlData }.
 *
 * Processing order (same as Main.js):
 *   1. Load all image files into the cache under every key variant.
 *   2. Parse the MTL file (if present) and log which textures resolved.
 */
async function buildMaterialLib(files, renderer) {
  const materialLib = {};
  let mtlData = null;

  const imageExts = /\.(png|jpg|jpeg|bmp|gif|tga|tif|tiff|webp)$/i;
  const imageFiles = files.filter((f) => imageExts.test(f.name));
  const mtlFile   = files.find((f) => f.name.toLowerCase().endsWith('.mtl'));

  // ── Step 1: load every image into the cache ──────────────────────────────
  for (const file of imageFiles) {
    // webkitRelativePath preserves folder structure (e.g. "mario/tex/body.png")
    const rel = file.webkitRelativePath || file.name;
    try {
      await loadTextureFromFile(materialLib, file, rel, renderer);
    } catch (_) {
      console.warn(`[ObjFunction] Failed to load texture: ${file.name}`);
    }
  }

  // ── Step 2: parse MTL ────────────────────────────────────────────────────
  if (mtlFile) {
    try {
      const text = await mtlFile.text();
      mtlData = parseMTL(text);
      // Log resolution status for every material's diffuse map
      for (const [name, props] of Object.entries(mtlData)) {
        if (props.map) {
          const resolved = !!findTexture(materialLib, props.map);
          console.log(
            `[ObjFunction] MTL material "${name}" → map_Kd "${props.map}" ${resolved ? '✅' : '❌ MISSING'}`
          );
        }
      }
    } catch (_) {
      console.warn('[ObjFunction] MTL parse failed');
    }
  }

  return { materialLib, mtlData };
}

/**
 * Apply the resolved texture / color from the material cache onto a Three.js
 * material that was created by OBJLoader.
 *
 * Rules (from Main.js):
 *  - Prefer the MTL map_Kd texture.
 *  - Only apply Kd color when there is NO texture (prevents orange-tint multiply).
 *  - If no MTL at all and only one texture exists, apply that single texture.
 *  - material.color = 0xffffff when a texture is present (must be pure white).
 */
function applyTextureToMaterial(material, materialLib, mtlData) {
  if (!material) return;

  const matName = material.name;
  const hasMTL  = !!mtlData;

  let tex     = null;
  let color   = null;
  let opacity = 1.0;

  if (hasMTL && mtlData[matName]) {
    const m = mtlData[matName];

    if (m.map) {
      tex = findTexture(materialLib, m.map);
    }

    // KEY: only apply Kd color when there is no texture — otherwise it
    // multiplies with the texture and tints it the wrong hue.
    if (!tex && m.color) {
      color = new Three.Color(m.color[0], m.color[1], m.color[2]);
    }

    if (m.opacity !== undefined) {
      opacity = m.opacity;
    }
  }

  // Fallback: no MTL at all + exactly one texture → use it for every mesh
  if (!tex && !hasMTL) {
    const keys = Object.keys(materialLib);
    if (keys.length > 0) tex = materialLib[keys[0]];
  }

  // Apply resolved values to the Three.js material
  if (tex) {
    material.map   = tex;
    material.color = new Three.Color(0xffffff); // pure white — must not tint texture
  } else if (color) {
    material.color = color;
  }

  if (opacity < 1) {
    material.opacity     = opacity;
    material.transparent = true;
  }

  material.needsUpdate = true;
}

// ──────────────────────────────────────────────────────────────
// MATERIAL PRESERVATION – aligned with typical Mesh.js (GLTF) pattern
// ──────────────────────────────────────────────────────────────
function preserveMaterial(material) {
  if (!material) return;

  material.needsUpdate = true;

  // NOTE: Do NOT force flipY = false on any maps here.
  // Textures loaded via loadTextureFromFile already have flipY = true (correct
  // for OBJLoader). Overriding to false would re-introduce the upside-down bug.
  // If a map exists and was loaded externally, leave its flipY as-is.
  if (material.map)          material.map.needsUpdate          = true;
  if (material.normalMap)    material.normalMap.needsUpdate    = true;
  if (material.roughnessMap) material.roughnessMap.needsUpdate = true;
  if (material.metalnessMap) material.metalnessMap.needsUpdate = true;
  if (material.aoMap)        material.aoMap.needsUpdate        = true;
  if (material.emissiveMap)  material.emissiveMap.needsUpdate  = true;

  // Deep backup of original properties (used for reset / material editor)
  if (!material.userData.originalMaterialProps) {
    material.userData.originalMaterialProps = {
      type: material.type,
      color: material.color ? material.color.getHex() : undefined,
      emissive: material.emissive ? material.emissive.getHex() : undefined,
      emissiveIntensity: material.emissiveIntensity ?? 1,
      metalness: material.metalness ?? 0,
      roughness: material.roughness ?? 1,
      opacity: material.opacity ?? 1,
      transparent: material.transparent ?? false,
      side: material.side ?? Three.FrontSide,
      depthWrite: material.depthWrite ?? true,
      blending: material.blending ?? Three.NormalBlending,
      wireframe: material.wireframe ?? false,
      map: material.map || null,
      normalMap: material.normalMap || null,
      roughnessMap: material.roughnessMap || null,
      metalnessMap: material.metalnessMap || null,
      aoMap: material.aoMap || null,
      emissiveMap: material.emissiveMap || null,
      alphaTest: material.alphaTest ?? 0,
      vertexColors: material.vertexColors ?? false,
    };
  }

  // Store original PBR values separately (very common in material editors)
  if (material.metalness !== undefined) {
    material.userData.originalMetalness = material.metalness;
  }
  if (material.roughness !== undefined) {
    material.userData.originalRoughness = material.roughness;
  }

  // Default structure expected by UpdateMaterial / material editor
  if (!material.userData.material_settings) {
    material.userData.material_settings = {
      albedo: material.color ? `#${material.color.getHexString()}` : '#FFFFFF',
      metallicType: material.metalness > 0.01,   // rough heuristic
      emissionType: (material.emissive?.r ?? 0) > 0.01 || (material.emissive?.g ?? 0) > 0.01 || (material.emissive?.b ?? 0) > 0.01,
      renderingMode: 'Opaque',
      added: true,
      // You can extend this object later with texture URLs, etc.
    };
  }
}

export function handleMeshObjUpload(
  e,
  {
    scene,
    transformControls,
    selectedObject,
    setPolyCount,
    renderer, // pass the Three.js WebGLRenderer so we can read max anisotropy
    GLTFLoader,
    DRACOLoader
  },
  setPendingUploads
) {
  const files = Array.from(e.target.files || []);
  if (!files.length) return;

  // Find OBJ file
  const objFile = files.find(f => f.name.toLowerCase().endsWith('.obj'));
  if (!objFile) {
    toast.error('No OBJ file found in folder.');
    return;
  }

  const maxSize = 500 * 1024 * 1024; // 500MB
  if (objFile.size > maxSize) {
    toast.error('OBJ file size should not exceed 500MB.', {
      duration: 3000,
      style: { background: '#1d1e21', color: '#fff' },
    });
    return;
  }

  selectedObject = null;
  store.dispatch(selected_object(null));
  store.dispatch(setLoadingMessage(true));

  const loader = new OBJLoader();
  const objectURL = URL.createObjectURL(objFile);
  const tempId = `temp-${Date.now()}-${objFile.name}`;

  // ── Build material lib FIRST, then load OBJ ──────────────────────────────
  buildMaterialLib(files, renderer).then(({ materialLib, mtlData }) => {
    loader.load(
      objectURL,
      function (object) {
        object.rotation.set(0, 0, 0);
        object.name = tempId;
        object.objPath = objFile;
        object.objType = '3d';
        object._id = tempId;
        object.glb_type = 'obj';
        object.originalFile = objFile;
        object.file_name = objFile.name;
        object.url = tempId;
        object.deleteType = 'mesh';

        // Center model
        const box = new Three.Box3().setFromObject(object);
        const center = new Three.Vector3();
        box.getCenter(center);
        object.position.sub(center);

        // ── Apply textures via resolved material lib ──────────────────────
        object.traverse((child) => {
          if (child.isMesh && child.material) {
            const applyAndPreserve = (mat) => {
              applyTextureToMaterial(mat, materialLib, mtlData);
              preserveMaterial(mat);
            };
            if (Array.isArray(child.material)) {
              child.material.forEach(applyAndPreserve);
            } else {
              applyAndPreserve(child.material);
            }
          }
        });

        // ── Export object to GLB and upload ──────────────────────
        normalizeImportedMeshMaterialsForExport(object);
        const exporter = new GLTFExporter();
        exporter.parse(
          object,
          (gltf) => {
            const blob = new Blob([gltf], { type: 'model/gltf-binary' });
            const glbFile = new File([blob], objFile.name.replace(/\.[^/.]+$/, '.glb'), { type: 'model/gltf-binary' });
            
            // Pass the new glbFile to handleMeshUpload
            handleMeshUpload(
              { target: { files: [glbFile] } },
              {
                GLTFLoader,
                DRACOLoader,
                scene,
                transformControls,
                selectedObject,
                setPolyCount,
              },
              setPendingUploads
            );

            // Cleanup OBJ loading state
            store.dispatch(setLoadingMessage(false));
            URL.revokeObjectURL(objectURL);
          },
          (error) => {
            console.error('GLTF Export failed', error);
            toast.error('Failed to convert OBJ to GLB.');
            store.dispatch(setLoadingMessage(false));
            URL.revokeObjectURL(objectURL);
          },
          { binary: true }
        );
      },
      undefined,
      function (error) {
        console.error('Failed to load OBJ:', error);
        store.dispatch(setLoadingMessage(false));
        URL.revokeObjectURL(objectURL);
        toast.error('Failed to load OBJ file');
      }
    );
  });
}

export function DownloadMeshObj(
  meshData,
  { scene, selectedObject, setPolyCount, renderer }
) {
  console.log(meshData, '-----> meshDataaa');

  meshData.forEach((mesh) => {
    const meshPath = mesh.asset_file_path;
    const position_x = mesh.position_x;
    const position_y = mesh.position_y;
    const position_z = mesh.position_z;
    const rotation_x = mesh.rotation_x;
    const rotation_y = mesh.rotation_y;
    const rotation_z = mesh.rotation_z;
    const scale_x = mesh.scale_x;
    const scale_y = mesh.scale_y;
    const scale_z = mesh.scale_z;

    const loader = new OBJLoader();

    fetch(meshPath)
      .then(response => response.blob())
      .then(async (blob) => {
        const objectURL = URL.createObjectURL(blob);

        store.dispatch(addModel({ url: objectURL, name: mesh.asset_file_path }));

        // ── For server-loaded OBJs, build a minimal materialLib from the
        //    blob URL itself. No texture files are available here, so we
        //    skip image loading and only apply preserveMaterial (same as
        //    the original DownloadMeshObj behaviour). If the server later
        //    provides a texture URL, extend this section accordingly.
        const materialLib = {};
        const mtlData     = null;

        loader.load(
          objectURL,
          function (object) {
            // Center model
            const box = new Three.Box3().setFromObject(object);
            const center = new Three.Vector3();
            box.getCenter(center);
            object.position.sub(center);

            object.name = mesh._id;
            object.url = mesh._id;
            object.deleteType = 'mesh';
            object.glb_type = 'obj';

            // Apply saved transforms
            object.position.set(position_x, position_y, position_z);
            object.rotation.set(
              Three.MathUtils.degToRad(rotation_x),
              Three.MathUtils.degToRad(rotation_y),
              Three.MathUtils.degToRad(rotation_z)
            );
            object.scale.set(scale_x, scale_y, scale_z);
            object.updateMatrix();
            object.updateMatrixWorld(true);

            // Preserve materials – same as Mesh.js
            object.traverse((child) => {
              if (child.isMesh && child.material) {
                const applyAndPreserve = (mat) => {
                  applyTextureToMaterial(mat, materialLib, mtlData);
                  preserveMaterial(mat);
                };
                if (Array.isArray(child.material)) {
                  child.material.forEach(applyAndPreserve);
                } else {
                  applyAndPreserve(child.material);
                }
              }
            });

            object.userData = {
              material_settings: mesh.material_settings || {},
              objType: '3d',
            };

            // Shadows & side
            object.traverse((child) => {
              if (child.isMesh) {
                child.castShadow = true;
                child.receiveShadow = true;
                if (child.material) {
                  child.material.side = Three.DoubleSide;
                  child.material.needsUpdate = true;
                }
              }
            });

            scene.add(object);
            selectedObject = object;

            setPolyCount((prevCount) => prevCount + 1);
            store.dispatch(setPushIntersectedObject(object));
            selectObjectInScene(mesh._id);
            store.dispatch(selected_object(object));
            store.dispatch(addSceneMesh(object));

            if (mesh.material_settings) {
              UpdateMaterial({ object, addData: mesh.material_settings });
            }

            URL.revokeObjectURL(objectURL);
          },
          undefined,
          function (error) {
            console.error('Error loading OBJ model:', error);
            URL.revokeObjectURL(objectURL);
          }
        );
      })
      .catch(error => {
        console.error('Error fetching OBJ file:', error);
      });
  });
}



export async function duplicateMeshObjAsset(
  item,
  { Three, scene, transformControls, selectedObject, renderer },
  setPendingUploads,
  originalMesh = null
) {
  if (!item || !item.asset_file_path || !item.file_name) {
    toast.error('Valid item with file path and name are required for duplication');
    return;
  }

  selectedObject = null;
  store.dispatch(selected_object(null));
  transformControls?.detach();

  store.dispatch(setLoadingMessage(true));

  const baseName = item.file_name.replace(/\.[^/.]+$/, '');
  const duplicateFileName =
    getNextCopyName(scene, baseName) +
    (item.file_name.match(/\.[^/.]+$/)?.[0] || '');

  const tempId = buildTempFileName(duplicateFileName);
  const duplicateMeshCode = `mesh_${Math.random().toString(36).substr(2, 6)}`;

  const loader = new OBJLoader();

  try {
    const response = await fetch(item.asset_file_path);
    const blob = await response.blob();
    const objectURL = URL.createObjectURL(blob);

    // ── For duplicated server assets, no local image files are available.
    //    Same fallback as DownloadMeshObj — preserveMaterial only.
    //    If the original mesh object carries a cached materialLib, it can
    //    be passed via originalMesh.materialLib and used here.
    const materialLib = originalMesh?.materialLib || {};
    const mtlData     = originalMesh?.mtlData     || null;

    loader.load(
      objectURL,
      function (duplicatedMesh) {
        // Center model
        const box = new Three.Box3().setFromObject(duplicatedMesh);
        const center = new Three.Vector3();
        box.getCenter(center);
        duplicatedMesh.position.sub(center);

        duplicatedMesh.name = tempId;
        duplicatedMesh.url = tempId;
        duplicatedMesh.deleteType = 'mesh';
        duplicatedMesh.glb_type = 'obj';
        isolateDuplicatePublishIdentity(duplicatedMesh);

        const sourceMesh = originalMesh || scene.getObjectByName(item?._id || item?.id || item?.url || item?.name);

        if (sourceMesh) {
          duplicatedMesh.position.set(
            sourceMesh.position.x,
            sourceMesh.position.y,
            sourceMesh.position.z
          );
          duplicatedMesh.rotation.copy(sourceMesh.rotation);
          duplicatedMesh.scale.copy(sourceMesh.scale);
        } else {
          duplicatedMesh.position.set(
            item.position_x || 0,
            item.position_y || 0,
            item.position_z || 0
          );

          duplicatedMesh.rotation.set(
            Three.MathUtils.degToRad(item.rotation_x || 0),
            Three.MathUtils.degToRad(item.rotation_y || 0),
            Three.MathUtils.degToRad(item.rotation_z || 0)
          );

          duplicatedMesh.scale.set(
            item.scale_x || 1,
            item.scale_y || 1,
            item.scale_z || 1
          );
        }

        duplicatedMesh.updateMatrix();
        duplicatedMesh.updateMatrixWorld(true);

        // Preserve materials – aligned with Mesh.js
        duplicatedMesh.traverse((child) => {
          if (child.isMesh && child.material) {
            const applyAndPreserve = (mat) => {
              applyTextureToMaterial(mat, materialLib, mtlData);
              preserveMaterial(mat);
            };
            if (Array.isArray(child.material)) {
              child.material.forEach(applyAndPreserve);
            } else {
              applyAndPreserve(child.material);
            }
          }
        });

        duplicatedMesh.userData = {
          material_settings: { ...(item.material_settings || {}) },
          project: item.project,
          user: item.user,
          asset_file_name: item.asset_file_name,
          file_name: duplicateFileName,
          asset_file_type: item.asset_file_type,
          asset_thumbnail: item.asset_thumbnail,
          position_x: duplicatedMesh.position.x,
          position_y: duplicatedMesh.position.y,
          position_z: duplicatedMesh.position.z,
          scale_x: duplicatedMesh.scale.x,
          scale_y: duplicatedMesh.scale.y,
          scale_z: duplicatedMesh.scale.z,
          rotation_x: item.rotation_x || 0,
          rotation_y: item.rotation_y || 0,
          rotation_z: item.rotation_z || 0,
          saved: false,
          mesh_code: duplicateMeshCode,
          originalId: item._id || item.id,
          isDuplicate: true,
          objType: '3d',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        attachClonedEventsToDuplicate(duplicatedMesh, {
          sourceItem: item,
          sourceObject: originalMesh,
          events: item.events,
        });

        duplicatedMesh.traverse((child) => {
          if (child.isMesh) {
            child.castShadow = true;
            child.receiveShadow = true;
            if (child.material) {
              child.material.side = Three.DoubleSide;
              child.material.needsUpdate = true;
            }
          }
        });

        if (setPendingUploads && originalMesh?.originalFile) {
          setPendingUploads((prev) => [
            ...prev,
            {
              file: originalMesh.originalFile,
              transform: {
                position_x: duplicatedMesh.position.x,
                position_y: duplicatedMesh.position.y,
                position_z: duplicatedMesh.position.z,
                rotation_x: Three.MathUtils.radToDeg(duplicatedMesh.rotation.x),
                rotation_y: Three.MathUtils.radToDeg(duplicatedMesh.rotation.y),
                rotation_z: Three.MathUtils.radToDeg(duplicatedMesh.rotation.z),
                scale_x: duplicatedMesh.scale.x,
                scale_y: duplicatedMesh.scale.y,
                scale_z: duplicatedMesh.scale.z,
              },
              tempId: tempId,
              itemData: duplicatedMesh.userData,
            },
          ]);
        }

        scene.add(duplicatedMesh);

        store.dispatch(setPushIntersectedObject(duplicatedMesh));
        store.dispatch(addSceneMesh(duplicatedMesh));
        store.dispatch(
          addModel({ url: objectURL, name: duplicateFileName })
        );

        const displayName =
          tempId.length > 30 ? `${tempId.substring(0, 30)}...` : tempId;
        toast.success(`Mesh duplicated as "${displayName}"`);

        const duplicateItemData = {
          ...item,
          _id: undefined,
          id: tempId,
          url: tempId,
          file_name: duplicateFileName,
          mesh_code: duplicateMeshCode,
          position_x: duplicatedMesh.position.x,
          position_y: duplicatedMesh.position.y,
          position_z: duplicatedMesh.position.z,
          saved: false,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          originalId: item._id || item.id,
          isDuplicate: true,
        };

        try {
          store.dispatch(
            update_assets_data_new({ type: 'mesh', data: [duplicateItemData] })
          );
        } catch (err) {
          console.warn('Failed to update assets_data immediately for duplicate:', err);
        }

        store.dispatch(set_is_glb_image_editoropen(true));
        store.dispatch(selected_object(duplicatedMesh));

        if (item.material_settings) {
          UpdateMaterial({ object: duplicatedMesh, addData: item.material_settings });
        }

        if (transformControls) {
          transformControls.attach(duplicatedMesh);
        }

        store.dispatch(setLoadingMessage(false));

        URL.revokeObjectURL(objectURL);
      },
      undefined,
      function (error) {
        console.error('Error duplicating obj:', error);
        toast.error('Failed to duplicate obj asset');
        store.dispatch(setLoadingMessage(false));
        URL.revokeObjectURL(objectURL);
      }
    );
  } catch (error) {
    console.error('Error fetching OBJ file for duplication:', error);
    toast.error('Failed to fetch obj file for duplication');
    store.dispatch(setLoadingMessage(false));
  }
}